/**
 * @author Artem Nakhodkin <artem.nakhodkin@racingpost.com>
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Svg from 'rp-react-svg-icon';
import { breakpointCodes } from 'rp-mq-breakpoints';
import CourseInfo from './../CourseInfo';
import CourseSchedule from './../CourseSchedule';

const CLASS_DISABLED = 'rh-cardsMatrix__chevron--disabled';
const CLASS_FIRST = 'rh-cardsMatrix__race--first';
const CLASS_SCROLLABLE = 'rh-cardsMatrix__races--scrollable';

function checkIfTouchScreen() {
    if ('ontouchstart' in document.documentElement) return true;
    return false;
}

function addFirstRaceElemClass(schedule, raceNumber) {
    const firstElem = schedule.querySelector(`.${CLASS_FIRST}`);

    if (firstElem) {
        firstElem.classList.remove(CLASS_FIRST);
    }
    schedule.children[raceNumber].classList.add(CLASS_FIRST);
}

function setDisabledClass(chevron, isActiveChevron) {
    if (!isActiveChevron && !chevron.classList.contains(CLASS_DISABLED)) {
        chevron.classList.add(CLASS_DISABLED);
    }

    if (isActiveChevron && chevron.classList.contains(CLASS_DISABLED)) {
        chevron.classList.remove(CLASS_DISABLED);
    }
}

function checkIfLeftChevronIsActive(schedule) {
    if (schedule.style.left.length > 0 && schedule.style.left.split('px')[0] !== '0') {
        return true;
    }

    return false;
}

function checkIfRightChevronIsActive(schedule, raceWidth, maxRaces) {
    if (
        schedule.style.left !== `${(maxRaces - schedule.childElementCount) * raceWidth}px` &&
        maxRaces < schedule.childElementCount
    ) {
        return true;
    }

    return false;
}
/* Logic for showing the next race in meeting is:
    * shifting starts when next race goes to the fists slot that is not fully visible
    * the next race should be in the last fully visible slot
    * first visible race should be half hidden
*/

function scrollToNextRace(schedule, isTouchScreen, raceWidth, maxRaces) {
    const passedRaces = schedule.querySelectorAll('.rh-cardsMatrix__race--result');

    if (isTouchScreen) {
        const nextRaceInOffsetLeft =
            passedRaces.length &&
            passedRaces[passedRaces.length - 1].offsetLeft + raceWidth;
        const visibleSlots = Math.floor(schedule.clientWidth / raceWidth);
        const slotsToShift =
            schedule.clientWidth % raceWidth > raceWidth / 2
            ? visibleSlots : visibleSlots - 1;

        if (nextRaceInOffsetLeft && schedule.clientWidth < nextRaceInOffsetLeft + raceWidth) {
            const step = (slotsToShift - 0.5) * raceWidth;
            schedule.scrollLeft = nextRaceInOffsetLeft - step;
        }
    } else if (passedRaces.length >= maxRaces) {
        const slotsToShift = schedule.childElementCount - passedRaces.length >= maxRaces ?
            passedRaces.length - (maxRaces - 1) :
            schedule.childElementCount - maxRaces;

        schedule.setAttribute('style', `left: ${(-slotsToShift) * raceWidth}px`);
        addFirstRaceElemClass(schedule, slotsToShift);
    }
}

function setScheduleState(schedule, raceWidth, maxRaces) {
    const leftChevron = schedule.parentNode.previousSibling;
    const rightChevron = schedule.parentNode.nextSibling;

    schedule.setAttribute('style', '');

    if (schedule.classList.contains(CLASS_SCROLLABLE)) {
        scrollToNextRace(schedule, false, raceWidth, maxRaces);
    }

    setDisabledClass(leftChevron, checkIfLeftChevronIsActive(schedule));
    setDisabledClass(rightChevron, checkIfRightChevronIsActive(schedule, raceWidth, maxRaces));
}

function getLeftOffset(schedule) {
    const leftOffset = schedule.style.left.split('px')[0];
    if (leftOffset.length > 0) {
        return Number(leftOffset);
    }
    return 0;
}

function getChevronFormTarget(target) {
    while (!target.classList || !target.classList.contains('rh-cardsMatrix__chevron')) {
        target = target.parentNode;
    }

    return target;
}

/**
 * Component that is used for rendering cardsMatrix
 */
class CardsMatrix extends Component {
    static get defaultProps() {
        return {
            racesMaxAmountExWide: 7,
            racesMaxAmount: 4,
            raceWidth: 90,
        };
    }

    componentDidMount() {
        const schedules = document.querySelectorAll('.rh-cardsMatrix__races');
        const isTouchScreen = checkIfTouchScreen();
        const maxRaces = this.getMaxRaces();
        const { raceWidth } = this.props;

        if (schedules && schedules.length > 0) {
            const isScrollable = this.props.courses.find(course => course.races.length > maxRaces);

            for (let i = 0; i < schedules.length; i += 1) {
                if (!isTouchScreen && isScrollable) {
                    setScheduleState(schedules[i], raceWidth, maxRaces);
                } else {
                    scrollToNextRace(schedules[i], isTouchScreen, raceWidth, maxRaces);
                }
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.currentBreakpointCode !== this.props.currentBreakpointCode &&
            !checkIfTouchScreen()
        ) {
            const schedules = document.querySelectorAll('.rh-cardsMatrix__races');
            const maxRaces = this.getMaxRaces();
            const isScrollable = this.props.courses.find(course => course.races.length > maxRaces);

            if (schedules && schedules.length > 0 && isScrollable) {
                for (let i = 0; i < schedules.length; i += 1) {
                    const firstElem = schedules[i].querySelector(`.${CLASS_FIRST}`);

                    if (firstElem) {
                        firstElem.classList.remove(CLASS_FIRST);
                    }

                    setScheduleState(schedules[i], this.props.raceWidth, maxRaces);
                }
            }
        }
    }

    onLeftChevronClick = (event) => {
        const leftChevron = getChevronFormTarget(event.target);
        const schedule = leftChevron.nextSibling.firstElementChild;
        const rightChevron = schedule.parentNode.nextSibling;
        const { raceWidth } = this.props;
        const maxRaces = this.getMaxRaces();

        if (checkIfLeftChevronIsActive(schedule)) {
            const newleftOffset = getLeftOffset(schedule) + raceWidth;

            schedule.setAttribute('style', `left: ${newleftOffset}px`);
            addFirstRaceElemClass(schedule, -newleftOffset / raceWidth);
            setDisabledClass(leftChevron, checkIfLeftChevronIsActive(schedule));
            setDisabledClass(
                rightChevron,
                checkIfRightChevronIsActive(schedule, raceWidth, maxRaces)
            );
        }
    }

    onRightChevronClick = (event) => {
        const rightChevron = getChevronFormTarget(event.target);
        const schedule = rightChevron.previousSibling.firstElementChild;
        const leftChevron = schedule.parentNode.previousSibling;
        const { raceWidth } = this.props;
        const maxRaces = this.getMaxRaces();

        if (checkIfRightChevronIsActive(schedule, raceWidth, maxRaces)) {
            const newleftOffset = getLeftOffset(schedule) - raceWidth;

            schedule.setAttribute('style', `left: ${newleftOffset}px`);
            addFirstRaceElemClass(schedule, -newleftOffset / raceWidth);

            setDisabledClass(leftChevron, checkIfLeftChevronIsActive(schedule));
            setDisabledClass(
                rightChevron,
                checkIfRightChevronIsActive(schedule, raceWidth, maxRaces)
            );
        }
    }

    getMaxRaces = () => {
        const { racesMaxAmountExWide, racesMaxAmount, currentBreakpointCode } = this.props;
        const { EXTRA_WIDE } = breakpointCodes;

        return currentBreakpointCode === EXTRA_WIDE ? racesMaxAmountExWide : racesMaxAmount;
    }
    render() {
        const { courses, date, isAnchorSet, prefTab, height, isExpandable } = this.props;
        const maxRaces = this.getMaxRaces();
        const isScrollable = courses.find(course => course.races.length > maxRaces);
        const styles = isExpandable ? { maxHeight: `${height}px` } : {};
        const isTouchScreen = checkIfTouchScreen();
        const chevronClassNames = 'rh-cardsMatrix__chevron rh-cardsMatrix__chevron--disabled';

        return (
            <div className="rh-cardsMatrix" id="cardsMatrix" style={styles}>
                {
                    courses.map(course => (
                        <div
                            className={`rh-cardsMatrix__course ${course.abandoned
                                ? 'rh-cardsMatrix__course--abandoned' : ''}`}
                            key={course.id}
                        >
                            <CourseInfo course={course} date={date} />
                            {
                                isScrollable && !isTouchScreen ?
                                    <div className="rh-cardsMatrix__scheduleContainer">
                                        <div
                                            className={chevronClassNames}
                                            onClick={this.onLeftChevronClick}
                                        >
                                            <Svg
                                                className="rh-arrow"
                                                iconName="left" size="Medium"
                                            />
                                        </div>
                                        <div className="rh-cardsMatrix__racesContainer">
                                            <CourseSchedule
                                                course={course}
                                                isAnchorSet={isAnchorSet}
                                                prefTab={prefTab}
                                                isScrollable={course.races.length > maxRaces}
                                            />
                                        </div>
                                        <div
                                            className={chevronClassNames}
                                            onClick={this.onRightChevronClick}
                                        >
                                            <Svg
                                                isInlined
                                                className="rh-arrow"
                                                iconName="right"
                                                size="Medium"
                                            />
                                        </div>
                                    </div> :
                                    <CourseSchedule
                                        course={course}
                                        isAnchorSet={isAnchorSet}
                                        prefTab={prefTab}
                                    />
                        }
                        </div>
                    ))
                }
            </div>
        );
    }
}

CardsMatrix.propTypes = {
    currentBreakpointCode: PropTypes.string.isRequired,
    /**
     * Maximum heigh of the Matrix
     */
    height: PropTypes.number.isRequired,
    /**
     * Limit of amount races shown
     */
    racesMaxAmount: PropTypes.number.isRequired,
    /**
     * Limit of amount races shown on extra wide screen
     */
    racesMaxAmountExWide: PropTypes.number.isRequired,
    /**
     * Width of race button
     */
    raceWidth: PropTypes.number.isRequired,
    /**
     * Is Matrix expandable
     */
    isExpandable: PropTypes.bool.isRequired,
    /**
     * List of courses
     */
    courses: PropTypes.arrayOf(
        PropTypes.shape({
            /**
             * Unique id
             */
            id: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string
            ]).isRequired,
            /**
             * Is course abandoned
             */
            abandoned: PropTypes.bool.isRequired,
            /**
             * Is Course all Weather
             */
            allWeather: PropTypes.bool.isRequired,
            /**
             * Surface type of AW track
             */
            surfaceType: PropTypes.string.isRequired,
            /**
             * Course colour
             * If there is no colour, there will be no specific class
             */
            colour: PropTypes.number,
            /**
             * Course name
             */
            name: PropTypes.string.isRequired,
            /**
             * Course country code
             */
            countryCode: PropTypes.string.isRequired,
            /**
             * Course meeting url
             */
            meetingUrl: PropTypes.string.isRequired,
            /**
             * Course hashname
             */
            hashName: PropTypes.string.isRequired,
            /**
             * Course type
             */
            meetingTypeCode: PropTypes.string.isRequired,
            /**
             * List of races
             */
            races: PropTypes.arrayOf(
                PropTypes.shape({
                    /**
                     * Is race abandoned
                     */
                    abandoned: PropTypes.bool.isRequired,
                    /**
                     * Race result
                     */
                    result: PropTypes.bool.isRequired,
                    /**
                     * Race time
                     */
                    link: PropTypes.string.isRequired,
                    /**
                     * Race title
                     */
                    title: PropTypes.string.isRequired,
                    /**
                     * Race time
                     */
                    time: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number
                    ]),
                    /**
                     * Race number of runners
                     */
                    runners: PropTypes.number.isRequired,
                    /**
                     * Has race offers
                     */
                    hasRaceOffers: PropTypes.bool.isRequired,
                    /**
                     * Is next race
                     */
                    isNextRace: PropTypes.bool.isRequired,
                    /**
                     * Is next race less than 30 min away
                     */
                    isThirthyMinToNextRace: PropTypes.bool.isRequired,
                    /**
                     * Is Race started
                     */
                    started: PropTypes.bool.isRequired,
                })
            )
        })
    ).isRequired,
    date: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    /**
     * Is anchor set to url
     */
    isAnchorSet: PropTypes.bool.isRequired,
    /**
     * Pref tab fro cards links
     */
    prefTab: PropTypes.string.isRequired,
};

export {
    checkIfTouchScreen,
    addFirstRaceElemClass,
    setDisabledClass,
    checkIfLeftChevronIsActive,
    checkIfRightChevronIsActive,
    scrollToNextRace,
    setScheduleState,
    getLeftOffset,
    getChevronFormTarget,
    CardsMatrix as default,
};



// WEBPACK FOOTER //
// ./src/js/Cards/components/CardsMatrix/CardsMatrix.js